import { EVENT_CATEGORIES } from "../types/event";
import { toIso } from "./eventDates";
import type { NewEventPayload } from "../hooks/useEventManager";

export interface RequestDraft {
	title: string;
	eventType: string | null;
	allDay: boolean;
	startDate: string;
	startTime: string;
	endDate: string;
	endTime: string;
	/** `Description.Short` */
	purpose: string;
	/** `Description.Long` */
	details: string;
	venue: "" | "onsite" | "offsite";
	/** `Location.Name` */
	locationName: string;
	/** The directory room chosen for an onsite event; null when typed or offsite. */
	roomKey: string | null;
	/** A street address chosen from the suggestions for an offsite event. */
	locationAddress: string;
	locationLat: string | null;
	locationLon: string | null;
	/** Ticked on the first step; unticked is the next Calendar Group meeting. */
	urgent: boolean;
	boarding: boolean;
	assessmentsChecked: boolean;
	pathway: boolean;
	kmAcknowledged: boolean;
	charged: boolean;
	howCharged: string;
	staffCover: boolean;
	budgeted: boolean;
}

export const ALL_DAY_START = "00:00";
export const ALL_DAY_END = "23:59";

export const addMinutes = (time: string, minutes: number): string => {
	const [h, m] = time.split(":").map(Number);
	if (Number.isNaN(h) || Number.isNaN(m)) return time;
	const total = (h * 60 + m + minutes) % (24 * 60);
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
};

export const emptyDraft = (): RequestDraft => ({
	title: "",
	eventType: null,
	allDay: true,
	startDate: "",
	startTime: ALL_DAY_START,
	endDate: "",
	endTime: ALL_DAY_END,
	purpose: "",
	details: "",
	venue: "onsite",
	locationName: "",
	roomKey: null,
	locationAddress: "",
	locationLat: null,
	locationLon: null,
	urgent: false,
	boarding: false,
	assessmentsChecked: false,
	pathway: false,
	kmAcknowledged: false,
	charged: false,
	howCharged: "",
	staffCover: false,
	budgeted: false,
});

export type StepKey =
	"when" | "about" | "where" | "students" | "cost" | "review";

export const STEP_LABELS: Record<StepKey, string> = {
	when: "When",
	about: "About",
	where: "Where",
	students: "Students",
	cost: "Cost",
	review: "Review",
};

/**
 * What the requester confirms before submitting, in the order shown. All must
 * be ticked. A function of the draft because the room item only means something
 * for an onsite event.
 */
export const submitChecklist = (d: RequestDraft): string[] => [
	"The dates, times and location are correct.",
	...(d.venue === "onsite"
		? [
				"I understand the room is not booked until the Calendar Committee approves the event.",
			]
		: []),
];

export const STEPS: StepKey[] = [
	"when",
	"about",
	"where",
	"students",
	"cost",
	"review",
];

const startOf = (d: RequestDraft) =>
	toIso(d.startDate, d.allDay ? ALL_DAY_START : d.startTime);
const endOf = (d: RequestDraft) =>
	toIso(d.endDate || d.startDate, d.allDay ? ALL_DAY_END : d.endTime);

/** The first thing wrong with a step, or "" when it can be left. */
export const stepError = (step: StepKey, d: RequestDraft): string => {
	switch (step) {
		case "when":
			if (!d.title.trim()) return "Give the event a title.";
			if (!d.startDate) return "Choose a start date.";
			if (new Date(endOf(d)).getTime() <= new Date(startOf(d)).getTime())
				return "The end must come after the start.";
			return "";
		case "about":
			if (!d.purpose.trim()) return "Say what the event is for.";
			if (!d.details.trim()) return "Add further details of the event.";
			return "";
		case "where":
			if (!d.venue) return "Choose onsite or offsite.";
			if (!d.locationName.trim()) return "Say where it will be held.";
			return "";
		case "students":
			if (d.boarding && !d.kmAcknowledged)
				return "Acknowledge the Boarding team requirement.";
			return "";
		case "cost":
			if (d.charged && !d.howCharged.trim()) return "Say how it is charged.";
			return "";
		default:
			return "";
	}
};

/** What `POST event/admin/` takes. Everything else follows in a save. */
export const toCreatePayload = (d: RequestDraft): NewEventPayload => ({
	Title: d.title.trim(),
	StartDateTime: startOf(d),
	EndDateTime: endOf(d),
	Category: EVENT_CATEGORIES[0].id,
	EventType: d.eventType || undefined,
	/* Accepted at creation, so the answers exist from the first write and a
	   failed follow-up save cannot lose them. */
	PreApproval: {
		Urgency: d.urgent ? "urgent" : "next_calendar_group",
		Location: d.venue,
		BoardingStudentsInvolved: d.boarding,
		AssessmentsClashChecked: d.assessmentsChecked,
		RepresentativePathway: d.pathway,
		KmBoardingAcknowledged: d.kmAcknowledged,
		Charged: d.charged,
		HowCharged: d.charged ? d.howCharged.trim() : "",
		StaffCoverRequired: d.staffCover,
		CostBudgeted: d.budgeted,
	},
});

/** The fields the create request cannot carry, written onto the new event. */
export const toDetailsPatch = (d: RequestDraft): Record<string, unknown> => ({
	Description: { Short: d.purpose.trim(), Long: d.details.trim() },
	Location: {
		Name: d.locationName.trim(),
		...(d.locationAddress
			? {
					Address: d.locationAddress,
					Lat: d.locationLat,
					Lon: d.locationLon,
				}
			: {}),
	},
});
